import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDateTime, statusBadgeClass, statusLabel } from '../utils/formatters';
import { ShoppingCart, Search, Plus, ChevronLeft, ChevronRight, FileText, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

function ReturnRow({ ret, onView }) {
  return (
    <tr>
      <td>
        <span className="font-medium text-gray-900 dark:text-white">{ret.return_number || ret.number || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDateTime(ret.date)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{ret.sale?.sale_number || ret.sale_number || '-'}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700 dark:text-gray-200">{ret.customer?.name || ret.sale?.customer?.name || '-'}</span>
        </div>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200 truncate max-w-[200px]">{ret.reason || '-'}</span>
      </td>
      <td>
        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(ret.total || 0)}</span>
      </td>
      <td>
        <span className={`badge ${statusBadgeClass(ret.status)}`}>{statusLabel(ret.status)}</span>
      </td>
      <td>
        <button onClick={() => onView(ret)} className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded">
          <FileText className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

export default function SalesReturnsPage() {
  const { hasPermission } = useAuth();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '', date_from: '', date_to: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [sales, setSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchReturns();
  }, [pagination.page, filters]);

  const fetchReturns = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/sales-returns', { params });
      if (res.data.success) {
        setReturns(res.data.data?.returns || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSales = async () => {
    try {
      const res = await api.get('/sales', { params: { status: 'completed', limit: 50 } });
      setSales(res.data.data?.sales || res.data.data || []);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSaleSelect = (saleId) => {
    const sale = sales.find(s => s.id === parseInt(saleId));
    if (sale) {
      setSelectedSale(sale);
      setReturnItems((sale.items || []).map(item => ({
        product_id: item.product_id,
        product_name: item.product_name || item.product?.name,
        sku: item.sku || item.product?.sku,
        qty_sold: item.qty,
        qty_returned: 0,
        unit_price: item.unit_price || item.price
      })));
    }
  };

  const updateReturnedQty = (index, value) => {
    const newItems = [...returnItems];
    newItems[index].qty_returned = parseFloat(value) || 0;
    setReturnItems(newItems);
  };

  const calculateTotal = () => {
    return returnItems.reduce((sum, item) => sum + ((parseFloat(item.qty_returned) || 0) * parseFloat(item.unit_price || 0)), 0);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!selectedSale) { toast.error('Pilih penjualan terlebih dahulu'); return; }
    const selectedItems = returnItems.filter(item => (item.qty_returned || 0) > 0);
    if (selectedItems.length === 0) { toast.error('Pilih setidaknya satu item untuk diretur'); return; }
    setSaving(true);
    try {
      const payload = {
        sale_id: selectedSale.id,
        reason,
        items: selectedItems.map(item => ({
          product_id: item.product_id,
          qty_returned: parseFloat(item.qty_returned),
          unit_price: parseFloat(item.unit_price)
        }))
      };
      const res = await api.post('/sales-returns', payload);
      if (res.data.success) {
        toast.success('Retur penjualan berhasil dibuat');
        setShowCreateModal(false);
        setSelectedSale(null);
        setReturnItems([]);
        setReason('');
        fetchReturns();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Retur Penjualan</h1>
          <p className="page-subtitle">Kelola retur barang dari pelanggan</p>
        </div>
        {hasPermission('create_sales_returns') && (
          <button onClick={() => { fetchAvailableSales(); setShowCreateModal(true); }} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" /> Buat Retur Baru
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari nomor retur, pelanggan..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <div>
              <select className="input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">Semua Status</option>
                <option value="draft">Draft</option>
                <option value="pending">Menunggu</option>
                <option value="approved">Disetujui</option>
                <option value="completed">Selesai</option>
                <option value="cancelled">Dibatalkan</option>
              </select>
            </div>
            <div>
              <input type="date" className="input" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            </div>
            <div>
              <input type="date" className="input" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </div>
            <div className="flex gap-2">
              <button onClick={fetchReturns} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', status: '', date_from: '', date_to: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : returns.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada retur penjualan.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nomor Retur</th>
                    <th>Tanggal</th>
                    <th>Nomor Penjualan</th>
                    <th>Pelanggan</th>
                    <th>Alasan</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {returns.map((r) => (
                    <ReturnRow
                      key={r.id}
                      ret={r}
                      onView={() => { setSelectedReturn(r); setShowDetailsModal(true); }}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Buat Retur Penjualan</h3>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="label">Pilih Penjualan <span className="text-danger-600">*</span></label>
                <select className="input" value={selectedSale?.id || ''} onChange={(e) => handleSaleSelect(e.target.value)} required>
                  <option value="">Pilih Penjualan (status: Selesai)</option>
                  {sales.map(sale => (
                    <option key={sale.id} value={sale.id}>
                      {sale.sale_number || sale.number} - {sale.customer?.name} (Total: {formatCurrency(sale.total)})
                    </option>
                  ))}
                </select>
              </div>

              {selectedSale && (
                <>
                  <div>
                    <label className="label">Pelanggan: <span className="text-gray-900 dark:text-white font-normal">{selectedSale.customer?.name}</span></label>
                  </div>

                  {returnItems.length > 0 && (
                    <div className="table-container">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Produk</th>
                            <th>SKU</th>
                            <th>Qty Terjual</th>
                            <th>Qty Retur</th>
                            <th>Harga Satuan</th>
                            <th>Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {returnItems.map((item, index) => (
                            <tr key={index}>
                              <td>{item.product_name}</td>
                              <td>{item.sku}</td>
                              <td>{item.qty_sold}</td>
                              <td>
                                <input
                                  type="number"
                                  className="input w-20"
                                  value={item.qty_returned}
                                  onChange={(e) => updateReturnedQty(index, e.target.value)}
                                  min="0"
                                  max={item.qty_sold}
                                  step="1"
                                />
                              </td>
                              <td>{formatCurrency(item.unit_price)}</td>
                              <td>{formatCurrency((item.qty_returned || 0) * item.unit_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div>
                    <label className="label">Alasan Retur <span className="text-danger-600">*</span></label>
                    <textarea className="input min-h-[60px]" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Jelaskan alasan retur..." rows={2} required />
                  </div>

                  <div className="text-right">
                    <span className="font-semibold text-lg">Total Retur: {formatCurrency(calculateTotal())}</span>
                  </div>
                </>
              )}

              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => { setShowCreateModal(false); setSelectedSale(null); setReturnItems([]); setReason(''); }} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving || !selectedSale} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Simpan Retur'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedReturn && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Detail Retur Penjualan</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label text-sm font-semibold">Nomor Retur:</label>
                  <p className="text-gray-900 dark:text-white">{selectedReturn.return_number || selectedReturn.number}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Tanggal:</label>
                  <p className="text-gray-900 dark:text-white">{formatDateTime(selectedReturn.date)}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Nomor Penjualan:</label>
                  <p className="text-gray-900 dark:text-white">{selectedReturn.sale?.sale_number || selectedReturn.sale_number}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Status:</label>
                  <span className={`badge ${statusBadgeClass(selectedReturn.status)}`}>{statusLabel(selectedReturn.status)}</span>
                </div>
                <div className="col-span-2">
                  <label className="label text-sm font-semibold">Alasan:</label>
                  <p className="text-gray-900 dark:text-white">{selectedReturn.reason || '-'}</p>
                </div>
                <div>
                  <label className="label text-sm font-semibold">Total Retur:</label>
                  <p className="text-gray-900 dark:text-white font-semibold">{formatCurrency(selectedReturn.total || 0)}</p>
                </div>
              </div>

              {selectedReturn.items && selectedReturn.items.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold mb-3">Item Diretur</h4>
                  <div className="table-container">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Produk</th>
                          <th>SKU</th>
                          <th>Qty Terjual</th>
                          <th>Qty Diretur</th>
                          <th>Harga Satuan</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedReturn.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.product_name || item.product?.name}</td>
                            <td>{item.sku || item.product?.sku}</td>
                            <td>{item.qty_sold || item.qty}</td>
                            <td>{item.qty_returned || item.qty}</td>
                            <td>{formatCurrency(item.unit_price || item.price)}</td>
                            <td>{formatCurrency(item.subtotal || ((item.qty_returned || item.qty) * (item.unit_price || item.price)))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button onClick={() => { setShowDetailsModal(false); setSelectedReturn(null); }} className="btn-primary flex-1">Tutup</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}